/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useMemo } from 'react';
import { Calendar, Users, FolderKanban } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { getRepoContext } from '../utils/repoContext';
import { ActiveProductIndicator } from '../components/common/ActiveProductIndicator';
import { TimelineTab } from '../components/project/TimelineTab';
import { ContactsTab } from '../components/project/ContactsTab';

export type ProjectTab = 'timeline' | 'contacts';

type ProjectTabConfig = {
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
};

export function Project() {
  const { activeProduct, activeRepoUrl } = useApp();
  const [activeTab, setActiveTab] = useState<ProjectTab>('timeline');

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl],
  );

  const tabConfig: Record<ProjectTab, ProjectTabConfig> = {
    timeline: { label: 'Timeline', icon: Calendar },
    contacts: { label: 'Contacts', icon: Users },
  };

  const tabOrder: ProjectTab[] = ['timeline', 'contacts'];

  return (
    <div className="container py-6">
      <div className="card">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <FolderKanban className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-semibold">Project</h1>
                <p className="text-sm text-muted-foreground">
                  Timeline, contacts, and context for your active product
                </p>
              </div>
            </div>
            <div className="h-8 w-px bg-border" />
            <ActiveProductIndicator />
          </div>
        </div>

        {repoContext.repoUrl && (
          <div className="mb-4 text-xs text-blue-600">
            Project for <span className="font-mono">{repoContext.repoUrl}</span>
            {repoContext.repoPath && (
              <span className="text-slate-600"> ({repoContext.repoPath})</span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '24px',
          gap: '4px'
        }}>
          {tabOrder.map((tab) => {
            const Icon = tabConfig[tab].icon;
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: 'none',
                  background: 'transparent',
                  borderBottom: isActive ? '2px solid #0066cc' : '2px solid transparent',
                  marginBottom: '-2px',
                  color: isActive ? '#0066cc' : '#6b7280',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon size={16} />
                {tabConfig[tab].label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'timeline' && <TimelineTab />}
        {activeTab === 'contacts' && <ContactsTab />}
      </div>
    </div>
  );
}
